<template>
  <BaseCard
    :heading="$t('insights.service_popularity')"
    :loading="loading"
    :minLoadingHeight="400"
  >
    <Flex
      direction="column"
      :class="[$style.base, { [$style.smallScreen]: $screen === 's', [$style.expanded]: expanded }]"
    >
      <div :class="$style.inner">
        <Chart
          v-if="chartData.length"
          :data="chartData"
        />
        <div
          v-else
          :class="$style.emptyState"
        >
          <img
            :class="$style.emptyVisual"
            src="/img/insights/empty-chart.svg"
          >
          <BaseText>{{ $t('insights.no_data') }}</BaseText>
        </div>
        <Flex
          v-if="chartData.length"
          direction="column"
        >
          <Flex
            v-for="(service, index) in chartData"
            :key="service.id"
            :gap=".5"
            :class="$style.serviceItem"
            v-test="'insights-service'"
          >
            <div
              :style="{ backgroundColor: service.color }"
              :class="$style.square"
            ></div>
            <BaseText bold>{{ index + 1 }}</BaseText>
            <div>
              <BaseText
                bold
                v-test="'insights-service-name'"
              >
                {{ service.name }}
              </BaseText>
              <BaseText
                size="s"
                v-test="'insights-service-count-price'"
              >
                {{ service.count }}
                {{ $t('global.items.appointment', service.count) }}-
                {{ filters.currency(service.totalPrice) }}
              </BaseText>
            </div>
          </Flex>
          <BaseText
            v-if="chartData.length > 5"
            @click="expanded = !expanded"
            v-test="'insights-services-expand'"
          >
            {{ expanded ? $t('global.show_less') : $t('global.show_more') }}
          </BaseText>
        </Flex>
      </div>
      <BaseCard gray>
        <Flex direction="column">
          <BaseLabel color="success">{{ $t('insights.pro_tip') }}</BaseLabel>
          <BaseHeading>{{ $t('insights.promotions.heading') }}</BaseHeading>
          <BaseText
            :routerLink="{ name: 'marketing-discounts' }"
            v-test="'insights-promotions-link'"
          >
            {{ $t('insights.promotions.button_text') }}
          </BaseText>
        </Flex>
      </BaseCard>
    </Flex>
  </BaseCard>
</template>

<script
  setup
  lang="ts"
>
import { useQuery } from '@vue/apollo-composable';
import { storeToRefs } from 'pinia';
import filters from '@/filters';
import { lightColor } from '@/helpers/formatting';
import { useLocationsStore } from '@/stores/locations';
import { useServiceGroupsStore } from '@/stores/service-groups';
import Chart from './Chart.vue';
import { GET_SERVICES_POPULARITY_INSIGHTS } from './graphql';

const { locationId, dataScope } = storeToRefs(useLocationsStore());
const startAt = inject<Ref<string>>('startAt')!;
const endAt = inject<Ref<string>>('endAt')!;

const { result, loading } = useQuery(GET_SERVICES_POPULARITY_INSIGHTS, () => ({
  dataScope: dataScope.value,
  locationId: locationId.value,
  startAt: startAt.value,
  endAt: endAt.value,
}));

const chartData = computed(() => {
  const { flattenedServices } = useServiceGroupsStore();
  return (
    result.value?.servicesPopularityInsights.services
      .map((service: { id: number; count: number; totalPrice: number }) => {
        const s = flattenedServices.find((s) => s.id === service.id);
        return s
          ? {
              id: service.id,
              name: s.name,
              count: service.count,
              color: s.category.color ? lightColor(s.category.color) : null,
              totalPrice: service.totalPrice,
            }
          : null;
      })
      .filter(Boolean) || []
  );
});

const expanded = ref(false);
</script>

<style
  lang="scss"
  module
>
.inner {
  display: flex;
  gap: $spacing;

  .base.smallScreen & {
    flex-direction: column;
  }

  .base:not(.smallScreen) & {
    align-items: center;
    
    & > * {
      flex: 1 0 50%;
    }
  }
}

.serviceItem {
  &:nth-child(n + 6) {
    .base:not(.expanded) & {
      display: none;
    }
  }
}

.square {
  width: 8px;
  height: 8px;
  margin-top: 6px;
}

.emptyState {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: $spacing;
  min-height: 180px;
}

.emptyVisual {
  width: 40px;
  height: 40px;
}
</style>
